import Hidden from '@material-ui/core/Hidden';
import Link from '@material-ui/core/Link';
import List from '@material-ui/core/List';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Typography from '@material-ui/core/Typography';
import logo from 'assets/images/almeida-junior.png';
import { RouterContext } from 'components/Router';
import Alert from 'components/Shared/Alert';
import Logos from 'components/Shared/Logos';
import { IAppRoute } from 'interfaces/route';
import { globalRoles, userRoles } from 'interfaces/user';
import React, { memo, useContext, useEffect, useState } from 'react';
import { useObservable } from 'react-use-observable';
import rxjsOperators from 'rxjs-operators';
import authService from 'services/auth';

import DrawerListItem from './ListItem';
import { IAppRouteParsed, routeParser } from './routeParser';
import AppDrawerUser from './UserMenu';
import VentureSelection from './VentureSelection';
import Version from './Version';

interface IProps {
  routes: IAppRoute[];
  classes?: any;
}

export interface IDrawerContext {
  open(): void;
  close(): void;
}

export const DrawerContext = React.createContext<IDrawerContext>(null);

const useStyles = makeStyles(theme => ({
  root: {
    background: theme.palette.primary.contrastText,
    color: theme.palette.primary.main,
    height: '100vh',
    display: 'flex',
    flexDirection: 'column'
  },
  header: {
    paddingTop: theme.spacing(1),
    textAlign: 'center',
    background: theme.palette.primary.contrastText
  },
  logo: {
    maxWidth: 170,
    maxHeight: 100,
    margin: `${theme.spacing(1)}px 0`
  },
  list: {
    padding: 0,
    flex: 1
  }
}));

const AppDrawer = (props: IProps) => {
  const classes = useStyles();
  const [user] = useObservable(() => authService.getUser().pipe(rxjsOperators.logError()), []);
  const getRouter = useContext(RouterContext);
  const drawer = useContext(DrawerContext);
  const [routes, setRoutes] = useState<IAppRouteParsed[]>([]);

  useEffect(() => {
    setRoutes(routeParser(props.routes));
  }, [props.routes]);

  const handleSuport = () => {
    const message = (
      <Typography color="textPrimary">
        Entre em contato através do e-mail<br />
        <a href="mailto:suporte@solidfiles.com.br">suporte@solidfiles.com.br</a> ou telefone{' '}
        <Hidden mdUp>
          <a href="tel:+5511999070809">(11) 9 9907-0809</a>
        </Hidden>
        <Hidden smDown>
          (11) 9 9907-0809
        </Hidden>
      </Typography>
    );
    Alert.show({ title: 'Suporte', message });
  };

  const toRoute = (route: IAppRoute) => {
    drawer.close();
    if (route.path === '/help/support') return handleSuport();
    getRouter().navigate(route.path);
  };

  const canSelectVenture = user && [...globalRoles, userRoles.auditor].some(role =>
    user.roles.includes(role)
  );

  return (
    <div className={classes.root}>

      <div className={classes.header}>
        <Link href="/">
          <img src={logo} className={classes.logo} alt="logo" draggable={false} />
        </Link>
        <AppDrawerUser user={user} />
        <Hidden smUp implementation='css'>
          <VentureSelection disabled={!canSelectVenture} />
        </Hidden>
      </div>

      <List className={classes.list}>
        {routes.map(route => (
          <DrawerListItem
            key={route.path}
            user={user}
            route={route}
            onClick={toRoute}
          />
        ))}
      </List>
      <Logos absolute={false} />
      <Version />
    </div>
  );
};

export default memo<IProps>(AppDrawer);